import { type QConfigTableData } from '@exam-management/common/types';
import {
  difficultyTagList,
  calculationTagList,
  questionPropertyMapper,
} from '@exam-management/common/utils';
import {
  QConfigStructureCategory,
  QConfigStructureConditionType,
  QConfigStructureType,
  type QConfigStructureConditionSingleTagGroup,
  type QConfigStructureConditionMultiTagGroup,
  type ConditionTag,
} from '@exam-management/graphql';

import { type QConfigStructureData } from '../../types';

function getQuestionData(questions: QConfigStructureData[]) {
  function getTagKeys(tags: ConditionTag[]) {
    const difficulty = tags.find((tag) => difficultyTagList.includes(tag.name));
    const calculation = tags.find((tag) => calculationTagList.includes(tag.name));

    if (difficulty && calculation) {
      const difficultyKey =
        questionPropertyMapper[difficulty.name as keyof typeof questionPropertyMapper];
      const calculationKey =
        questionPropertyMapper[calculation.name as keyof typeof questionPropertyMapper];

      return {
        difficultyKey,
        calculationKey,
      };
    }
  }

  const {
    totalSingleQuestionsCount,
    totalItemSetQuestionsCount,
    questionProperty: { calculated, notCalculated },
  } = questions.reduce(
    (acc, question) => {
      if (question.category === QConfigStructureCategory.Single) {
        acc.totalSingleQuestionsCount += question.totalQuestions;
      } else if (question.category === QConfigStructureCategory.ItemSet) {
        acc.totalItemSetQuestionsCount += question.totalQuestions;
      }

      if (question.conditions) {
        const questionProperty = acc.questionProperty;

        for (const condition of question.conditions) {
          if (condition.type === QConfigStructureConditionType.SingleTagGroup) {
            const { needTotal, tags } = condition as QConfigStructureConditionSingleTagGroup;

            const totalQuestions = needTotal;

            const { difficultyKey, calculationKey } = getTagKeys(tags) ?? {};
            if (difficultyKey && calculationKey) {
              questionProperty[calculationKey][difficultyKey] =
                (questionProperty[calculationKey][difficultyKey] ?? 0) + totalQuestions;
            }
          } else if (condition.type === QConfigStructureConditionType.MultiTagGroup) {
            const { needTotal, tagGroups } = condition as QConfigStructureConditionMultiTagGroup;
            for (const tagGroup of tagGroups) {
              const { tags, count } = tagGroup;
              const totalQuestions = needTotal * count;
              const { difficultyKey, calculationKey } = getTagKeys(tags) ?? {};
              if (difficultyKey && calculationKey) {
                questionProperty[calculationKey][difficultyKey] =
                  (questionProperty[calculationKey][difficultyKey] ?? 0) + totalQuestions;
              }
            }
          }
        }
      }

      return acc;
    },
    {
      totalSingleQuestionsCount: 0,
      totalItemSetQuestionsCount: 0,
      questionProperty: {
        calculated: { easy: null, medium: null, hard: null },
        notCalculated: { easy: null, medium: null, hard: null },
      } as Record<string, Record<string, number | null>>,
    },
  );

  const questionType = {
    single: totalSingleQuestionsCount ?? 0,
    itemSet: totalItemSetQuestionsCount ?? 0,
  };

  return {
    questionType,
    calculated,
    notCalculated,
  };
}

function findParentIds(
  data: QConfigStructureData[],
  parentId: string | null,
  parentIds: string[] = [],
): string[] {
  if (parentId === null) {
    return parentIds;
  }
  const parent = data.find((item) => item.id === parentId);
  if (parent) {
    return findParentIds(data, parent.parentId, [...parentIds, parent.id]);
  }
  return parentIds;
}

export function formatChapterDataToTableData(
  levelItems: QConfigStructureData[],
  item: QConfigStructureData,
  qConfigStructure: QConfigStructureData[],
) {
  const childrenLevelItems = levelItems.filter((levelItem) => levelItem.parentId === item.id);

  const questionData = getQuestionData(childrenLevelItems);
  const parentIds = findParentIds(qConfigStructure, item.parentId);

  return {
    id: item.id,
    name: item.name,
    parentIds,
    parentId: item.parentId,
    parentRefId: item.refId,
    refCode: item.refCode,
    refId: item.refId,
    type: item.type,
    category: item.category,
    totalQuestions: item.totalQuestions,
    scorePerQuestion: item.score,
    ...questionData,
  };
}

export function formatSubjectDataToTableData(
  levelItems: QConfigStructureData[],
  item: QConfigStructureData,
  qConfigStructure: QConfigStructureData[],
) {
  let totalSingleQuestionsCount = 0;
  let totalItemSetQuestionsCount = 0;
  const groupLevelItems = levelItems.filter((levelItem) => levelItem.parentId === item.id);

  const allChapterLevelItemsUnderSubject = [];

  for (const groupLevelItem of groupLevelItems) {
    const chapterLevelItems = levelItems.filter(
      (levelItem) => levelItem.parentId === groupLevelItem.id,
    );
    for (const chapterLevelItem of chapterLevelItems) {
      const chapterTableData = formatChapterDataToTableData(
        levelItems,
        chapterLevelItem,
        qConfigStructure,
      );
      totalSingleQuestionsCount += chapterTableData?.questionType.single ?? 0;
      totalItemSetQuestionsCount += chapterTableData?.questionType.itemSet ?? 0;
    }
    allChapterLevelItemsUnderSubject.push(...chapterLevelItems);
  }
  const chapterIds = allChapterLevelItemsUnderSubject
    .map((chapter) => chapter.id)
    .filter((chapterId) => chapterId !== null);
  const questions = qConfigStructure
    .filter((item) => item.type === QConfigStructureType.QuestionType)
    .filter(({ parentId }) => parentId && chapterIds.includes(parentId));

  const questionData = getQuestionData(questions);
  const parentIds = findParentIds(qConfigStructure, item.parentId);

  return {
    id: item.id,
    name: item.name,
    parentId: item.parentId,
    parentRefId: item.refId,
    parentIds,
    refCode: item.refCode,
    refId: item.refId,
    type: item.type,
    category: item.category,
    totalQuestions: item.totalQuestions,
    scorePerQuestion: item.score,
    ...questionData,
    questionType: {
      single: totalSingleQuestionsCount,
      itemSet: totalItemSetQuestionsCount,
    },
  };
}

export function formatOtherLevelsDataToTableData(
  item: QConfigStructureData,
  qConfigStructure: QConfigStructureData[],
) {
  const questions = qConfigStructure.filter(
    (question) =>
      (question.type === QConfigStructureType.QuestionType ||
        question.type === QConfigStructureType.Tag) &&
      (question.id === item.id || question.id === item.parentId),
  );

  const questionData = getQuestionData(questions);

  const parentIds = findParentIds(qConfigStructure, item.parentId);

  return {
    id: item.id,
    name: item.name,
    parentIds,
    refCode: item.refCode,
    parentRefId: item.refId,
    refId: item.refId,
    parentId: item.parentId,
    type: item.type,
    category: item.category,
    totalQuestions: item.totalQuestions,
    scorePerQuestion: item.score,
    size: item.size,
    ...questionData,
  };
}

export function formatLOSTableData(
  item: QConfigStructureData,
  qConfigStructure: QConfigStructureData[],
) {
  const questions = qConfigStructure.filter(
    (question) =>
      (question.type === QConfigStructureType.QuestionType ||
        question.type === QConfigStructureType.Tag) &&
      (question.id === item.id || question.id === item.parentId),
  );

  const questionData = getQuestionData(questions);

  const parentIds = findParentIds(qConfigStructure, item.parentId);

  const parentQuestion = qConfigStructure.find((question) => question.id === item.parentId);

  return {
    id: item.id,
    name: item.name,
    parentIds,
    parentId: item.parentId,
    refCode: item.refCode,
    refId: item.refId,
    type: item.type,
    category: item.category,
    totalQuestions: parentQuestion?.totalQuestions ?? item.totalQuestions,
    scorePerQuestion: parentQuestion?.score ?? item.score,
    ...questionData,
  };
}

export function formatQConfigStructureToTableData(qConfigStructure: QConfigStructureData[]) {
  const levelItems = qConfigStructure.filter((item) =>
    [
      QConfigStructureType.QBankStructureLevelItem,
      QConfigStructureType.QuestionType,
      QConfigStructureType.Tag,
    ].includes(item.type),
  );
  return levelItems
    .filter((item) => item.category !== null)
    .map((item) => {
      switch (item.category) {
        case QConfigStructureCategory.Subject: {
          return formatSubjectDataToTableData(levelItems, item, qConfigStructure);
        }
        case QConfigStructureCategory.Chapter: {
          return formatChapterDataToTableData(levelItems, item, qConfigStructure);
        }
        case QConfigStructureCategory.LearningObjectiveStatement: {
          return formatLOSTableData(item, qConfigStructure);
        }
        default: {
          return formatOtherLevelsDataToTableData(item, qConfigStructure);
        }
      }
    }) as QConfigTableData[];
}
